import { Txt } from "@stenajs-webui/core";
import { Label } from "@stenajs-webui/elements";
import { addMonths } from "date-fns";
import type { FC } from "react";

import { DetailsFieldCell } from "../../../../../common-ui/details/DetailsFieldCell";
import { HiddenFieldGate } from "../../../../../common-ui/HiddenFieldGate";
import { getShortArrivalInformation } from "../../../../../common-ui/sailing-info/utils/getExtendedArrivalInformation";
import { DepartureTimeDropdownOption } from "../../../../../common/departure-dropdown/DepartureTimeDropdownOption";
import { useAllRoutesAsOptionsWithFavouriteOptions } from "../../../../../common/graphql/useAllRoutesAsOptionsWithFavouriteOptions";
import { useDepartureTimeOptions } from "../../../../../common/graphql/useDepartureTimeOptions";
import { useLocalDateFormat } from "../../../../../common/hooks/useLocalDateFormat";
import {
  type BookingFields,
  isHidden,
} from "../../../../../common/utils/bookingFields";
import { formatServerDate } from "../../../../../common/utils/dates/dateUtils";
import {
  transformRoute,
  transformSailing,
} from "../../../../../common/utils/optionTransformers";
import { ColumnId } from "../../../../../gql/graphql";
import type { BookingFormState } from "../../../../../services/update-form/updateFormReducer";
import { DepartureDateInputField } from "../DepartureDateInputField";
import { UpdateValidationWrapper } from "../edit/UpdateValidationWrapper";
import { DropdownField } from "../fields/DropdownField";
import { BookingSection } from "./BookingSection";

interface RouteDateSailingSectionProps {
  disabled: boolean;
  fieldsToEnable: BookingFields;
  fieldsToShow: BookingFields;
  formState: BookingFormState;
  hasArrived: boolean;
}

export const SailingDetailsSection: FC<RouteDateSailingSectionProps> = ({
  hasArrived,
  disabled,
  fieldsToShow,
  fieldsToEnable,
  formState,
}) => {
  const [allRoutesAsOptionsWithFavoriteOptions] =
    useAllRoutesAsOptionsWithFavouriteOptions();
  const [departureTimeOptions] = useDepartureTimeOptions(
    formState?.departureDate,
    formState?.route?.code,
  );

  const isDisabled = (field: ColumnId) =>
    disabled || isHidden(fieldsToEnable, field);

  const dateFormat = useLocalDateFormat();

  // TODO: remove arrival information from update modal since arrived bookings cannot be edited?
  const arrivalInformation = formState.sailing
    ? getShortArrivalInformation(hasArrived, formState.sailing, dateFormat)
    : undefined;

  return (
    <BookingSection title={"Route"}>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.RouteCode)}>
        <DetailsFieldCell
          label="Route"
          renderField={(id) => (
            <DropdownField
              id={id}
              trackerCategory={"Details"}
              name={"route"}
              transformOption={transformRoute}
              disabled={isDisabled(ColumnId.RouteCode)}
              options={allRoutesAsOptionsWithFavoriteOptions}
            />
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.DepartureDate)}>
        <DetailsFieldCell
          label="Date"
          renderField={(id) => (
            <UpdateValidationWrapper
              name={"departureDate"}
              renderField={(isValid) => (
                <DepartureDateInputField
                  id={id}
                  name={"departureDate"}
                  fromDate={formatServerDate(new Date())}
                  routeCode={formState.route ? formState.route.code : null}
                  toDate={formatServerDate(addMonths(new Date(), 6))}
                  disabled={isDisabled(ColumnId.DepartureDate)}
                  width={"100%"}
                  trackerCategory={"Details"}
                  variant={!isValid ? "error" : undefined}
                />
              )}
            />
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.DepartureTime)}>
        <DetailsFieldCell
          label="Departure time"
          renderField={(id) => (
            <UpdateValidationWrapper
              name={"sailing"}
              renderField={(isValid) => {
                return (
                  <DropdownField
                    id={id}
                    trackerCategory={"Details"}
                    name={"sailing"}
                    transformOption={transformSailing}
                    disabled={isDisabled(ColumnId.DepartureTime)}
                    options={departureTimeOptions}
                    components={components}
                    variant={!isValid ? "error" : undefined}
                  />
                );
              }}
            />
          )}
        />
        {arrivalInformation && (
          <Label text={"Arrival time"}>
            <Txt>{arrivalInformation}</Txt>
          </Label>
        )}
      </HiddenFieldGate>
    </BookingSection>
  );
};

const components = {
  Option: DepartureTimeDropdownOption,
};
